import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  XMarkIcon,
  TrashIcon,
  DocumentTextIcon,
  TagIcon,
  UserIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
  UserGroupIcon,
  BoltIcon,
  BeakerIcon,
  CogIcon,
  WrenchIcon,
  ShieldCheckIcon,
  ArrowsRightLeftIcon,
  ArrowUpIcon,
  ArrowDownIcon,
  DocumentIcon,
  PhotoIcon,
  PencilIcon,
  CheckIcon,
  PlusIcon,
  PaperClipIcon,
  HashtagIcon,
  SparklesIcon,
  ArrowPathIcon
} from '@heroicons/react/24/outline';
import apiClient from '../../utils/apiClient';
import ConfirmationModal from '../ConfirmationModal';
import DocumentDetailModal from './DocumentDetailModal';
import InvoiceMatcher from '../InvoiceMatcher';
import AttributeLabel from '../AttributeLabel';
import type { AttributeIconKey } from '../../utils/attributeIcons';
import Combobox from '../Combobox';
import { ModalShell, ModalActions } from './core';
import type { ModalCloseReason } from './core';

/** Mögliche Gegenbuchung zu einer nachträglich zu kennzeichnenden Umbuchung. */
interface Kandidat {
  id: number;
  buchungstag: string;
  betrag: number;
  buchungstext: string;
  verwendungszweck: string | null;
  name_zahlungsbeteiligter: string | null;
  konto_id: number;
  konto_name: string;
  tage_abstand: number;
}

interface StatementDetailModalProps {
  statementId: string;
  onClose: () => void;
  onStatementUpdate?: (statement: any) => void;
  onStatementDelete?: (statementId: string) => void;
}

interface Category {
  id: number;
  kategorie_id: number;
  betrag: number;
  member_id?: number;
  category_name?: string;
  kategorie_beschreibung?: string;
  member_name?: string;
  beitrag_jahr?: number;
}

interface CategoryMaster {
  id: number;
  name: string;
  icon?: string;
  steuerliche_sphaere?: string;
  beschreibung?: string;
}

interface CategorySuggestion {
  category_id: number;
  category_name: string;
  category_description?: string | null;
  confidence: number;
  needs_review: boolean;
  reason: string;
  signals: string[];
  /** Nur bei Jev: Wahrscheinlichkeit in Prozent je Kategorie-ID. */
  probabilities?: Record<string, number>;
}

interface Document {
  id: number;
  name: string;
  invoice_amount: number;
  upload_date: string;
  rechnung_pfad?: string;
  betrag?: number;
  file_type?: string;
  file_size?: number;
}

const normalizeStatementDocuments = (data: any) => {
  const seenDocumentIds = new Set<number>();
  const uniqueDocuments = (Array.isArray(data?.documents) ? data.documents : []).filter(
    (document: Document) => {
      if (!document?.id || seenDocumentIds.has(document.id)) return false;
      seenDocumentIds.add(document.id);
      return true;
    },
  );

  return { ...data, documents: uniqueDocuments };
};

type DetailTab = 'details' | 'cats' | 'docs';

/**
 * Abschnittskarte aus dem Entwurf „Buchung Detail": Kopfzeile mit kleinem
 * Outline-Icon und Versalien-Label, optional rechts eine Kennzahl, darunter
 * der Inhalt. Farben/Radien kommen aus dem Projekt-Designsystem.
 */
const SectionCard: React.FC<{
  icon: React.ComponentType<{ className?: string; 'aria-hidden'?: boolean | 'true' | 'false' }>;
  title: React.ReactNode;
  right?: React.ReactNode;
  className?: string;
  bodyClassName?: string;
  children: React.ReactNode;
}> = ({ icon: Icon, title, right, className = '', bodyClassName = 'space-y-2.5 p-4 sm:p-[18px]', children }) => (
  <div
    className={`overflow-hidden rounded-2xl border border-gray-200 bg-white dark:border-gray-700 dark:bg-[#1c2432] ${className}`}
  >
    <div className="flex items-center justify-between gap-3 border-b border-gray-200 px-4 py-3 dark:border-gray-700 sm:px-[18px]">
      <span className="flex min-w-0 items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.06em] text-gray-500 dark:text-gray-400">
        <Icon className="h-[15px] w-[15px] flex-shrink-0" aria-hidden="true" />
        <span className="truncate">{title}</span>
      </span>
      {right}
    </div>
    <div className={bodyClassName}>{children}</div>
  </div>
);

/** Schalter im Entwurfsmaß (46×28, Knopf 22px) für „Als Umbuchung kennzeichnen". */
const SwitchButton: React.FC<{
  checked: boolean;
  disabled?: boolean;
  label: string;
  onClick: () => void;
}> = ({ checked, disabled = false, label, onClick }) => (
  <button
    type="button"
    role="switch"
    aria-checked={checked}
    aria-label={label}
    disabled={disabled}
    onClick={onClick}
    className={`flex h-7 w-[46px] flex-shrink-0 items-center rounded-full p-[3px] transition-colors disabled:cursor-not-allowed disabled:opacity-50 ${
      checked ? 'bg-primary-600' : 'bg-gray-300 dark:bg-gray-600'
    }`}
  >
    <span
      className={`block h-[22px] w-[22px] rounded-full bg-white shadow-sm transition-transform duration-150 motion-reduce:transition-none ${
        checked ? 'translate-x-[18px]' : 'translate-x-0'
      }`}
    />
  </button>
);

/** Gestrichelter Aktionsknopf („Kategorie zuordnen" / „Beleg hinzufügen"). */
const DashedAction: React.FC<{
  icon: React.ComponentType<{ className?: string; 'aria-hidden'?: boolean | 'true' | 'false' }>;
  onClick: () => void;
  children: React.ReactNode;
}> = ({ icon: Icon, onClick, children }) => (
  <button
    type="button"
    onClick={onClick}
    className="flex h-12 w-full items-center justify-center gap-2 rounded-xl border border-dashed border-gray-300 bg-white text-[13.5px] font-semibold text-primary-600 transition-colors hover:bg-primary-50 dark:border-gray-600 dark:bg-transparent dark:text-primary-400 dark:hover:bg-primary-900/20 sm:h-[42px] sm:text-[13px]"
  >
    <Icon className="h-4 w-4" aria-hidden="true" />
    {children}
  </button>
);

const StatementDetailModal: React.FC<StatementDetailModalProps> = ({
  statementId,
  onClose,
  onStatementUpdate,
  onStatementDelete
}) => {
  const [statement, setStatement] = useState<any>(null);
  const [categories, setCategories] = useState<Category[]>([]);
  const [documents, setDocuments] = useState<Document[]>([]);
  const [categoryMasters, setCategoryMasters] = useState<CategoryMaster[]>([]);
  const [members, setMembers] = useState<any[]>([]);
  const [accounts, setAccounts] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // UI State
  // Mobil segmentiert der Entwurf in Details/Kategorien/Belege; ab `sm` steht
  // alles gleichzeitig in zwei Spalten und der Tab-Zustand ist wirkungslos.
  const [activeTab, setActiveTab] = useState<DetailTab>('details');
  const [isAddingCategory, setIsAddingCategory] = useState(false);
  const [selectedCategoryId, setSelectedCategoryId] = useState(0);
  const [categoryAmount, setCategoryAmount] = useState('');
  const [categoryMemberId, setCategoryMemberId] = useState<number | null>(null);
  const [categoryBeitragJahr, setCategoryBeitragJahr] = useState<number>(new Date().getFullYear());
  const [showInvoiceMatcher, setShowInvoiceMatcher] = useState(false);
  const [showDocumentDetail, setShowDocumentDetail] = useState<number | null>(null);
  const [statementTags, setStatementTags] = useState<string[]>([]);
  const [availableBuchungTags, setAvailableBuchungTags] = useState<string[]>([]);
  const [showTagPicker, setShowTagPicker] = useState(false);
  const [newTagInput, setNewTagInput] = useState('');
  const [categorySuggestion, setCategorySuggestion] = useState<CategorySuggestion | null>(null);
  const [categorySuggestionLoading, setCategorySuggestionLoading] = useState(false);
  const [categorySuggestionError, setCategorySuggestionError] = useState<string | null>(null);

  // Umbuchung nachtraeglich kennzeichnen
  const [umbuchungOffen, setUmbuchungOffen] = useState(false);
  const [kandidaten, setKandidaten] = useState<Kandidat[]>([]);
  const [ladeKandidaten, setLadeKandidaten] = useState(false);
  const [zielKontoId, setZielKontoId] = useState<number | null>(null);
  const [gegenbuchungId, setGegenbuchungId] = useState<number | null>(null);
  const [umbuchungLaeuft, setUmbuchungLaeuft] = useState(false);
  const [umbuchungFehler, setUmbuchungFehler] = useState<string | null>(null);

  // Edit Mode State
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [editData, setEditData] = useState({
    buchungstag: '',
    buchungstext: '',
    verwendungszweck: '',
    name_zahlungsbeteiligter: '',
    betrag: '',
    erstellt_am: '',
  });

  const sortedCategories = [...categoryMasters].sort((a, b) => a.name.localeCompare(b.name, 'de'));
  const sortedMembers = [...members].sort((a, b) => `${a.vorname} ${a.name}`.localeCompare(`${b.vorname} ${b.name}`, 'de'));

  // ConfirmationModal State
  const [confirmModal, setConfirmModal] = useState({
    visible: false,
    type: 'warning' as 'success' | 'warning' | 'error' | 'question' | 'info',
    title: '',
    message: '',
    variant: 'two' as 'single' | 'two',
  });


  useEffect(() => {
    if (statementId) {
      setCategorySuggestion(null);
      setCategorySuggestionError(null);
      loadStatementDetails();
      loadCategories();
      loadMembers();
      loadAccounts();
      loadBuchungTags();
    }
  }, [statementId]);

  const loadStatementDetails = async () => {
    try {
      setLoading(true);
      const response = await apiClient.get(`/api/finance/statements/${statementId}`);
      const normalizedStatement = normalizeStatementDocuments(response.data);
      setStatement(normalizedStatement);
      setCategories(normalizedStatement.categories || []);
      setDocuments(normalizedStatement.documents);
      setStatementTags(normalizedStatement.tags || []);
    } catch (err) {
      console.error('Error loading statement details:', err);
      setError('Buchungsdetails konnten nicht geladen werden');
    } finally {
      setLoading(false);
    }
  };

  const loadBuchungTags = async () => {
    try {
      const response = await apiClient.get('/api/finance/buchung-tags');
      setAvailableBuchungTags((response.data || []).map((t: any) => t.tag));
    } catch (err) {
      console.error('Error loading buchung tags:', err);
    }
  };

  const handleAddTag = async (tag: string) => {
    const trimmed = tag.trim();
    if (!trimmed || statementTags.includes(trimmed)) return;
    setStatementTags(prev => [...prev, trimmed]);
    try {
      await apiClient.post(`/api/finance/statements/${statementId}/tags`, { tag: trimmed });
      if (!availableBuchungTags.includes(trimmed)) {
        setAvailableBuchungTags(prev => [...prev, trimmed]);
      }
      if (onStatementUpdate && statement) {
        onStatementUpdate({ ...statement, tags: [...statementTags, trimmed] });
      }
    } catch (err) {
      console.error('Error adding tag:', err);
      setStatementTags(prev => prev.filter(t => t !== trimmed));
    }
  };

  const handleRemoveTag = async (tag: string) => {
    setStatementTags(prev => prev.filter(t => t !== tag));
    try {
      await apiClient.delete(`/api/finance/statements/${statementId}/tags/${encodeURIComponent(tag)}`);
      if (onStatementUpdate && statement) {
        onStatementUpdate({ ...statement, tags: statementTags.filter(t => t !== tag) });
      }
    } catch (err) {
      console.error('Error removing tag:', err);
      setStatementTags(prev => [...prev, tag]);
    }
  };

  const loadCategories = async () => {
    try {
      const response = await apiClient.get('/api/finance/categories');
      setCategoryMasters(response.data);
    } catch (err) {
      console.error('Failed to load categories:', err);
    }
  };

  const loadMembers = async () => {
    try {
      const response = await apiClient.get('/api/finance/members');
      setMembers(response.data);
    } catch (err) {
      console.error('Failed to load members:', err);
    }
  };

  const loadAccounts = async () => {
    try {
      const response = await apiClient.get('/api/finance/accounts');
      setAccounts(response.data);
    } catch (err) {
      console.error('Failed to load accounts:', err);
    }
  };

  const handleClose = (_reason?: ModalCloseReason) => {
    onClose();
  };

  // Format currency with +/- sign and EUR suffix
  const formatAmount = (amount: number) => {
    const absAmount = Math.abs(amount);
    const formatted = new Intl.NumberFormat('de-DE', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(absAmount);
    const sign = amount < 0 ? '-' : '+';
    return `${sign}${formatted} EUR`;
  };

  const formatCurrency = (amount: number) =>
    new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(amount || 0);

  const formatDate = (dateStr: string) =>
    new Date(dateStr).toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });

  const getRemainingAmount = useCallback(() => {
    if (!statement) return 0;
    const totalCategorized = categories.reduce((sum, cat) => sum + Math.round(Math.abs(Number(cat.betrag)) * 100), 0) / 100;
    return (Math.round(Math.abs(Number(statement.betrag)) * 100) - Math.round(totalCategorized * 100)) / 100;
  }, [statement, categories]);

  const isTransfer = useCallback(() => {
    return statement && statement.von_konto_id && statement.nach_konto_id;
  }, [statement]);

  const getAccountName = useCallback((accountId: number) => {
    const account = accounts.find(acc => acc.id === accountId);
    return account ? account.name : 'Unbekanntes Konto';
  }, [accounts]);

  /* ---------------------------------------------------------------- *
   * Umbuchung nachträglich kennzeichnen
   *
   * Eine Umbuchung besteht aus zwei Zeilen. Beim Import entstehen daraus
   * zwei unabhängige Buchungen, die dann in Einnahmen UND Ausgaben zählen,
   * obwohl das Geld nur das Konto gewechselt hat. Hier werden die beiden
   * vorhandenen Zeilen lediglich gekennzeichnet — Betrag, Datum, Zweck,
   * Belege und Kategorien bleiben unverändert.
   * ---------------------------------------------------------------- */

  const umbuchungOeffnen = useCallback(async () => {
    setUmbuchungOffen(true);
    setUmbuchungFehler(null);
    setZielKontoId(null);
    setGegenbuchungId(null);
    setLadeKandidaten(true);
    try {
      const res = await apiClient.get(`/api/finance/statements/${statementId}/umbuchung-kandidaten`);
      const gefunden: Kandidat[] = res.data?.kandidaten || [];
      setKandidaten(gefunden);
      // Gibt es genau eine passende Gegenbuchung, ist sie fast immer die
      // richtige — dann gleich vorauswählen, samt Zielkonto.
      if (gefunden.length === 1) {
        setGegenbuchungId(gefunden[0].id);
        setZielKontoId(gefunden[0].konto_id);
      }
    } catch (err) {
      console.error('Umbuchung: Kandidaten konnten nicht geladen werden', err);
      setKandidaten([]);
    } finally {
      setLadeKandidaten(false);
    }
  }, [statementId]);

  const umbuchungSpeichern = useCallback(async () => {
    if (!zielKontoId) return;
    setUmbuchungLaeuft(true);
    setUmbuchungFehler(null);
    try {
      await apiClient.post(`/api/finance/statements/${statementId}/umbuchung`, {
        ziel_konto_id: zielKontoId,
        gegenbuchung_id: gegenbuchungId,
      });
      const res = await apiClient.get(`/api/finance/statements/${statementId}`);
      setStatement(res.data);
      if (onStatementUpdate) onStatementUpdate(res.data);
      setUmbuchungOffen(false);
    } catch (err: any) {
      setUmbuchungFehler(err?.response?.data?.error || 'Die Umbuchung konnte nicht gesetzt werden.');
    } finally {
      setUmbuchungLaeuft(false);
    }
  }, [statementId, zielKontoId, gegenbuchungId, onStatementUpdate]);

  const umbuchungAufheben = useCallback(async () => {
    setUmbuchungLaeuft(true);
    try {
      const res = await apiClient.delete(`/api/finance/statements/${statementId}/umbuchung`);
      const neu = await apiClient.get(`/api/finance/statements/${statementId}`);
      setStatement(neu.data);
      if (onStatementUpdate) onStatementUpdate(neu.data);
      // Bei Altbestand ohne Verknüpfung lässt sich die Gegenzeile nicht
      // eindeutig bestimmen — dann bleibt sie stehen und muss von Hand
      // nachgezogen werden. Das darf nicht stillschweigend passieren.
      if (res.data?.unvollstaendig) {
        setConfirmModal({
          visible: true,
          type: 'info',
          title: 'Nur diese Buchung zurückgesetzt',
          message:
            'Die zugehörige Gegenbuchung war nicht eindeutig bestimmbar und wurde nicht angefasst. ' +
            'Bitte prüfe sie auf dem anderen Konto von Hand.',
          variant: 'single',
        });
      }
    } catch (err: any) {
      setConfirmModal({
        visible: true,
        type: 'error',
        title: 'Aufheben fehlgeschlagen',
        message: err?.response?.data?.error || 'Die Kennzeichnung konnte nicht aufgehoben werden.',
        variant: 'single',
      });
    } finally {
      setUmbuchungLaeuft(false);
    }
  }, [statementId, onStatementUpdate]);

  const getCategoryIcon = useCallback((kategorie_id: number) => {
    const iconMap: { [key: number]: any } = {
      1: UserGroupIcon,
      2: BoltIcon,
      3: BeakerIcon,
      4: CogIcon,
      5: WrenchIcon,
      6: ShieldCheckIcon,
      14: ArrowsRightLeftIcon,
    };
    return iconMap[kategorie_id] || TagIcon;
  }, []);

  const getCategoryName = useCallback((kategorie_id: number) => {
    const category = categoryMasters.find(cat => cat.id === kategorie_id);
    return category ? category.name : `Kategorie ${kategorie_id}`;
  }, [categoryMasters]);

  const getMemberName = useCallback((member_id: number) => {
    const member = members.find(m => m.member_id === member_id);
    return member ? `${member.vorname} ${member.name}` : `Mitglied ${member_id}`;
  }, [members]);

  const addCategory = async () => {
    if (!selectedCategoryId || !categoryAmount) return;
    if (selectedCategoryId === 1 && !categoryMemberId) return;

    const amount = Number(categoryAmount);
    if (!Number.isFinite(amount) || amount <= 0 || Math.abs(amount * 100 - Math.round(amount * 100)) > 0.000001) {
      alert('Bitte einen positiven Kategorie-Betrag mit höchstens zwei Nachkommastellen eingeben.');
      return;
    }
    const remainingAmount = getRemainingAmount();

    if (Math.round(amount * 100) > Math.round(remainingAmount * 100)) {
      alert(`Betrag überschreitet den verfügbaren Restbetrag`);
      return;
    }

    try {
      await apiClient.post(`/api/finance/statements/${statementId}/categories`, {
        kategorie_id: selectedCategoryId,
        betrag: amount,
        member_id: categoryMemberId,
        beitrag_jahr: selectedCategoryId === 1 ? categoryBeitragJahr : null,
      });

      const response = await apiClient.get(`/api/finance/statements/${statementId}`);
      const normalizedStatement = normalizeStatementDocuments(response.data);
      setStatement(normalizedStatement);
      setCategories(normalizedStatement.categories || []);
      setDocuments(normalizedStatement.documents);
      if (onStatementUpdate) {
        onStatementUpdate(normalizedStatement);
      }

      setIsAddingCategory(false);
      setSelectedCategoryId(0);
      setCategoryAmount('');
      setCategoryMemberId(null);
      setCategoryBeitragJahr(new Date().getFullYear());
    } catch (err: any) {
      console.error('Error adding category:', err);
      alert(err?.response?.data?.error || 'Die Kategorie konnte nicht gespeichert werden.');
    }
  };

  const askCategoryAssistant = async () => {
    setCategorySuggestionLoading(true);
    setCategorySuggestionError(null);
    try {
      const response = await apiClient.post(`/api/finance/statements/${statementId}/suggest-category`);
      if (!response.data?.success || !response.data?.suggestion) {
        setCategorySuggestionError(response.data?.error || 'Die KI hat keinen Vorschlag geliefert.');
        return;
      }
      const vorschlag: CategorySuggestion = response.data.suggestion;
      setCategorySuggestion(vorschlag);
      // Vorschlag direkt im Formular vorauswählen; gespeichert wird erst mit „Hinzufügen".
      setSelectedCategoryId(vorschlag.category_id);
      if (vorschlag.category_id !== 1) setCategoryMemberId(null);
      setCategoryAmount(getRemainingAmount().toFixed(2));
      setCategoryBeitragJahr(new Date(statement?.buchungstag || Date.now()).getFullYear());
      setIsAddingCategory(true);
    } catch (err: any) {
      console.error('StatementDetailModal: Kategorie-Vorschlag fehlgeschlagen', err);
      setCategorySuggestionError(
        err?.response?.data?.error || 'Der Kategorie-Vorschlag konnte nicht erstellt werden.'
      );
    } finally {
      setCategorySuggestionLoading(false);
    }
  };

  // Ohne Kategorie fragt das Modal beim Öffnen einmal selbst nach einem Vorschlag.
  const autoVorschlagFuer = useRef<string | null>(null);
  useEffect(() => {
    if (!statement || Number(statement.id) !== Number(statementId)) return;
    if (autoVorschlagFuer.current === String(statementId)) return;
    autoVorschlagFuer.current = String(statementId);
    const offen = categories.length === 0 && Math.abs(Number(statement.betrag) || 0) > 0.004;
    if (!offen || statement.von_konto_id || statement.nach_konto_id) return;
    // Formular sofort zeigen; der Jev-Vorschlag wählt die Kategorie nach.
    setCategoryAmount(getRemainingAmount().toFixed(2));
    setCategoryBeitragJahr(new Date(statement.buchungstag || Date.now()).getFullYear());
    setIsAddingCategory(true);
    askCategoryAssistant();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statement, statementId]);

  const deleteCategory = async (categoryId: number) => {
    try {
      await apiClient.delete(`/api/finance/statements/${statementId}/categories/${categoryId}`);
      const response = await apiClient.get(`/api/finance/statements/${statementId}`);
      const normalizedStatement = normalizeStatementDocuments(response.data);
      setStatement(normalizedStatement);
      setCategories(normalizedStatement.categories || []);
      setDocuments(normalizedStatement.documents);
      if (onStatementUpdate) {
        onStatementUpdate(normalizedStatement);
      }
    } catch (err) {
      console.error('Error deleting category:', err);
    }
  };

  const deleteDocument = async (documentId: number) => {
    try {
      await apiClient.delete(`/api/finance/statements/${statementId}/documents/${documentId}`);
      const response = await apiClient.get(`/api/finance/statements/${statementId}`);
      const normalizedStatement = normalizeStatementDocuments(response.data);
      setStatement(normalizedStatement);
      setCategories(normalizedStatement.categories || []);
      setDocuments(normalizedStatement.documents);
      if (onStatementUpdate) {
        onStatementUpdate(normalizedStatement);
      }
    } catch (err) {
      console.error('Error deleting document:', err);
    }
  };

  const handleDeleteClick = () => {
    setConfirmModal({
      visible: true,
      type: 'warning',
      title: 'Buchung löschen?',
      message: 'Diese Aktion kann nicht rückgängig gemacht werden.',
      variant: 'two',
    });
  };

  const confirmDeleteStatement = async () => {
    try {
      await apiClient.delete(`/api/finance/statements/${statementId}`);
      if (onStatementDelete) onStatementDelete(statementId);

      setConfirmModal({
        visible: true,
        type: 'success',
        title: 'Erfolgreich',
        message: 'Die Buchung wurde erfolgreich gelöscht.',
        variant: 'single',
      });

      setTimeout(() => {
        handleClose();
      }, 1500);
    } catch (err) {
      console.error('Error deleting statement:', err);
      setConfirmModal({
        visible: true,
        type: 'error',
        title: 'Fehler',
        message: 'Die Buchung konnte nicht gelöscht werden.',
        variant: 'single',
      });
    }
  };

  const handleDocumentClick = (documentId: number) => {
    setShowDocumentDetail(documentId);
  };

  const handleDocumentDelete = async (documentId: number) => {
    await deleteDocument(documentId);
    setShowDocumentDetail(null);
  };

  /**
   * KI-Analyse für einen Beleg aus der Kontoauszugsansicht anstoßen.
   *
   * Vorher lud diese Funktion nur die Ansicht neu, ohne je eine Analyse
   * auszulösen — der Knopf im Detail-Modal war hier wirkungslos.
   *
   * Verhält sich jetzt wie `sendToAI` in der Dokumentenverwaltung: analysieren,
   * die Vorschläge übernehmen, dann neu laden. Die Route speichert Aussteller,
   * Steuerangaben und Zahlungsdaten selbst; hier werden nur Name, Tags, Betrag
   * und Datum nachgezogen.
   */
  const handleDocumentAIScan = async (docId: number) => {
    try {
      // Analyse braucht bis zu einer Minute - Client-Standard von 30 s reicht nicht.
      const res = await apiClient.post('/api/documents/ask-ai', { document_id: docId }, { timeout: 180000 });

      if (!res.data?.success) {
        setConfirmModal({
          visible: true,
          type: 'error',
          title: 'KI-Analyse fehlgeschlagen',
          message: res.data?.error || 'Ein unbekannter Fehler ist aufgetreten.',
          variant: 'single',
        });
        return;
      }

      const { description, tags, suggestedName, invoice_amount, invoice_date } = res.data;
      const uebernahme: Record<string, unknown> = {};
      if (suggestedName) uebernahme.name = suggestedName;
      if (Array.isArray(tags) && tags.length) uebernahme.tag = tags.join(',');
      if (description) uebernahme.ai_comment = description;
      if (invoice_amount !== null && invoice_amount !== undefined) uebernahme.invoice_amount = invoice_amount;
      if (invoice_date) uebernahme.invoice_date = invoice_date;

      if (Object.keys(uebernahme).length > 0) {
        await apiClient.put(`/api/documents/${docId}`, uebernahme);
      }
    } catch (err) {
      console.error('StatementDetailModal: KI-Analyse fehlgeschlagen', err);
      setConfirmModal({
        visible: true,
        type: 'error',
        title: 'KI-Analyse fehlgeschlagen',
        message: 'Die Analyse konnte nicht ausgeführt werden.',
        variant: 'single',
      });
    } finally {
      await loadStatementDetails();
    }
  };

  const handleDocumentDownload = async (docId: number) => {
    const documentUri = `/api/documents/download/${docId}`;
    window.open(documentUri, '_blank');
  };

  const handleFilesSelected = async (files: any[]) => {
    try {
      const currentDocumentIds = new Set(documents.map((document) => document.id));
      const selectedDocumentIds = new Set<number>(files.map((document) => document.id));
      const documentIdsToAdd = Array.from(selectedDocumentIds).filter(
        (documentId) => !currentDocumentIds.has(documentId),
      );
      const documentIdsToRemove = Array.from(currentDocumentIds).filter(
        (documentId) => !selectedDocumentIds.has(documentId),
      );

      await Promise.all([
        ...(documentIdsToAdd.length > 0
          ? [
              apiClient.post(`/api/finance/statements/${statementId}/documents`, {
                document_ids: documentIdsToAdd,
              }),
            ]
          : []),
        ...documentIdsToRemove.map((documentId) =>
          apiClient.delete(`/api/finance/statements/${statementId}/documents/${documentId}`),
        ),
      ]);

      const response = await apiClient.get(`/api/finance/statements/${statementId}`);
      const normalizedStatement = normalizeStatementDocuments(response.data);
      setStatement(normalizedStatement);
      setCategories(normalizedStatement.categories || []);
      setDocuments(normalizedStatement.documents);
      if (onStatementUpdate) {
        onStatementUpdate(normalizedStatement);
      }
      setShowInvoiceMatcher(false);
    } catch (err) {
      console.error('Error linking documents:', err);
      throw err;
    }
  };

  const toInputDate = (dateStr: string) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return '';
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  };

  const enterEditMode = () => {
    if (!statement) return;
    setEditData({
      buchungstag: toInputDate(statement.buchungstag),
      buchungstext: statement.buchungstext || '',
      verwendungszweck: statement.verwendungszweck || '',
      name_zahlungsbeteiligter: statement.name_zahlungsbeteiligter || '',
      betrag: String(Math.abs(statement.betrag)),
      erstellt_am: toInputDate(statement.erstellt_am),
    });
    setIsEditing(true);
  };

  const cancelEdit = () => {
    setIsEditing(false);
  };

  const saveEdit = async () => {
    if (!statement) return;
    const amount = Number(editData.betrag);
    const allocatedCents = categories.reduce((sum, cat) => sum + Math.round(Math.abs(Number(cat.betrag)) * 100), 0);
    if (!Number.isFinite(amount) || editData.betrag.trim() === '' || amount < 0
        || Math.abs(amount * 100 - Math.round(amount * 100)) > 0.000001) {
      alert('Bitte einen gültigen Betrag mit höchstens zwei Nachkommastellen eingeben.');
      return;
    }
    if (Math.round(amount * 100) < allocatedCents) {
      alert('Der Buchungsbetrag darf nicht kleiner als die Summe der Kategorie-Beträge sein.');
      return;
    }
    setIsSaving(true);
    try {
      const sign = statement.betrag < 0 ? -1 : 1;
      await apiClient.patch(`/api/finance/statements/${statementId}`, {
        buchungstag: editData.buchungstag,
        buchungstext: editData.buchungstext,
        verwendungszweck: editData.verwendungszweck,
        name_zahlungsbeteiligter: editData.name_zahlungsbeteiligter,
        betrag: sign * parseFloat(editData.betrag),
        erstellt_am: editData.erstellt_am,
      });
      // Reload statement
      const response = await apiClient.get(`/api/finance/statements/${statementId}`);
      const normalizedStatement = normalizeStatementDocuments(response.data);
      setStatement(normalizedStatement);
      setCategories(normalizedStatement.categories || []);
      setDocuments(normalizedStatement.documents);
      if (onStatementUpdate) {
        onStatementUpdate(normalizedStatement);
      }
      setIsEditing(false);
    } catch (err: any) {
      console.error('Error saving statement:', err);
      alert(err?.response?.data?.error || 'Die Buchung konnte nicht gespeichert werden.');
    } finally {
      setIsSaving(false);
    }
  };

  if (!statement && !loading) return null;

  const isExpense = statement?.betrag < 0;
  const remainingAmount = getRemainingAmount();
  const totalCategorized = categories.reduce((sum, cat) => sum + Math.round(Math.abs(Number(cat.betrag)) * 100), 0) / 100;

  // Status calculation
  const getStatus = () => {
    if (remainingAmount === 0 && totalCategorized > 0) return 'complete';
    if (totalCategorized > 0) return 'partial';
    return 'open';
  };
  const status = getStatus();

  const transfer = Boolean(isTransfer());
  const absBetrag = Math.abs(statement?.betrag || 0);
  const pct = absBetrag > 0 ? Math.min(100, Math.round((totalCategorized / absBetrag) * 100)) : 0;
  const hasRest = !transfer && remainingAmount > 0.004;
  const kontoName =
    accounts.find((a) => a.id === statement?.konto_id)?.name || statement?.kontotyp || 'Konto';

  // Entwurf: „Offen"/„Teilweise" warnen, „Zugeordnet" ist grün, „Umbuchung" neutral-blau.
  const statusMeta = transfer
    ? {
        label: 'Umbuchung',
        badge: 'bg-info-50 text-info-700 dark:bg-info-900/25 dark:text-info-300',
        bar: 'bg-info-500',
        text: 'text-info-700 dark:text-info-300',
      }
    : status === 'complete'
      ? {
          label: 'Zugeordnet',
          badge: 'bg-success-50 text-success-700 dark:bg-success-900/25 dark:text-success-300',
          bar: 'bg-success-500',
          text: 'text-success-700 dark:text-success-300',
        }
      : {
          label: status === 'partial' ? 'Teilweise' : 'Offen',
          badge: 'bg-warning-50 text-warning-700 dark:bg-warning-900/25 dark:text-warning-300',
          bar: 'bg-warning-500',
          text: 'text-warning-700 dark:text-warning-300',
        };

  const amountColor = transfer
    ? 'text-gray-600 dark:text-gray-300'
    : isExpense
      ? 'text-error-600 dark:text-error-400'
      : 'text-success-600 dark:text-success-400';

  // Bei Umbuchungen gibt es weder Kategorien noch Belege – dann bleibt nur
  // „Details" und der Tag-Reiter.
  const tabs: { key: DetailTab; label: string }[] = transfer
    ? [
        { key: 'details', label: 'Details' },
        { key: 'docs', label: 'Tags' },
      ]
    : [
        { key: 'details', label: 'Details' },
        { key: 'cats', label: `Kategorien (${categories.length})` },
        { key: 'docs', label: `Belege (${documents.length})` },
      ];
  const currentTab: DetailTab = tabs.some((t) => t.key === activeTab) ? activeTab : 'details';
  /** Mobil nur der aktive Reiter, ab `sm` immer sichtbar. */
  const tabPane = (key: DetailTab) => (currentTab === key ? '' : 'hidden sm:block');

  // `stack`: mobil steht der Wert unter dem Label, weil Verwendungszwecke aus
  // dem Bankimport sonst in einer 100-px-Spalte umbrechen.
  const detailRows: {
    key: string;
    iconKey: AttributeIconKey;
    label: string;
    value: React.ReactNode;
    stack?: boolean;
  }[] = statement
    ? [
        {
          key: 'buchungstext',
          iconKey: 'description',
          label: 'Buchungstext',
          value: statement.buchungstext || '—',
        },
        {
          key: 'verwendungszweck',
          iconKey: 'purpose',
          label: 'Verwendungszweck',
          value: statement.verwendungszweck || (
            <span className="italic text-gray-400 dark:text-gray-500">Kein Verwendungszweck</span>
          ),
          stack: true,
        },
        ...(statement.name_zahlungsbeteiligter
          ? [
              {
                key: 'beteiligter',
                iconKey: 'name' as AttributeIconKey,
                label: 'Zahlungsbeteiligter',
                value: statement.name_zahlungsbeteiligter,
              },
            ]
          : []),
        { key: 'konto', iconKey: 'bankAccount', label: 'Konto', value: kontoName },
        {
          key: 'buchungstag',
          iconKey: 'date',
          label: 'Buchungsdatum',
          value: formatDate(statement.buchungstag),
        },
        ...(statement.erstellt_am
          ? [
              {
                key: 'erstellt',
                iconKey: 'date' as AttributeIconKey,
                label: 'Erstellt am',
                value: formatDate(statement.erstellt_am),
              },
            ]
          : []),
      ]
    : [];

  return (
    <>
      <ModalShell
        open={Boolean(statementId)}
        onClose={handleClose}
        variant="detail"
        size="xl"
        accent={isExpense ? 'danger' : 'brand'}
        icon={isExpense ? ArrowUpIcon : ArrowDownIcon}
        title={
          statement ? `${isExpense ? 'Auszahlung' : 'Einzahlung'} · ${kontoName}` : 'Buchungsdetail'
        }
        subtitle={
          statement
            ? `${formatDate(statement.buchungstag)} · Beleg-Nr. ${statement.id ?? statementId}`
            : undefined
        }
        closeOnBackdrop={false}
        bodyClassName="!p-0 bg-gray-50 dark:bg-gray-900/40"
        footer={
          statement && !loading ? (
            isEditing ? (
              <div className="flex items-center gap-3">
                <span className="hidden min-w-0 flex-1 truncate text-xs text-gray-500 dark:text-gray-400 sm:block">
                  Nicht gespeicherte Änderungen
                </span>
                <div className="min-w-0 flex-1 sm:flex-none">
                  <ModalActions
                    secondary={{ label: 'Abbrechen', onClick: cancelEdit }}
                    primary={{
                      label: isSaving ? 'Speichern...' : 'Speichern',
                      onClick: saveEdit,
                      loading: isSaving,
                      icon: <CheckIcon className="w-4 h-4" />,
                    }}
                  />
                </div>
              </div>
            ) : (
              <div className="flex items-center gap-2.5">
                <button
                  type="button"
                  onClick={handleDeleteClick}
                  aria-label="Buchung löschen"
                  className="flex h-12 w-12 flex-shrink-0 items-center justify-center gap-2 rounded-xl border border-error-100 bg-error-50 text-sm font-semibold text-error-700 transition-colors hover:bg-error-100 dark:border-error-900/40 dark:bg-error-900/20 dark:text-error-300 dark:hover:bg-error-900/30 sm:h-11 sm:w-auto sm:px-4"
                >
                  <TrashIcon className="h-[18px] w-[18px]" aria-hidden="true" />
                  <span className="hidden sm:inline">Löschen</span>
                </button>
                <span className="hidden min-w-0 flex-1 truncate text-xs text-gray-500 dark:text-gray-400 sm:block">
                  Änderungen werden sofort gespeichert
                </span>
                <div className="min-w-0 flex-1 sm:flex-none">
                  <ModalActions secondary={{ label: 'Schließen', onClick: handleClose }} />
                </div>
              </div>
            )
          ) : undefined
        }
      >
        {loading ? (
          <div className="flex h-64 items-center justify-center">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-2 border-primary-200 border-t-primary-600 mx-auto mb-4" />
              <p className="text-gray-600 dark:text-gray-400">Laden...</p>
            </div>
          </div>
        ) : error ? (
          <div className="flex h-64 items-center justify-center">
            <div className="text-center">
              <ExclamationTriangleIcon className="h-12 w-12 text-error-600 dark:text-error-400 mx-auto mb-4" />
              <p className="text-error-600 dark:text-error-400">{error}</p>
              <button
                onClick={loadStatementDetails}
                className="mt-4 px-6 py-2.5 bg-primary-600 text-white rounded-xl hover:bg-primary-700 transition-colors font-medium"
              >
                Erneut versuchen
              </button>
            </div>
          </div>
        ) : statement ? (
          <div className="min-h-full p-4 sm:p-5">
            {/* Hero: Betrag, Status, Bearbeiten, Kategorisierungs-Fortschritt */}
            <div className="rounded-2xl border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-[#1c2432] sm:p-[18px]">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-[11px] font-semibold uppercase tracking-[0.06em] text-gray-400 dark:text-gray-500">
                    Betrag
                  </p>
                  {isEditing ? (
                    <div className="mt-1.5 flex items-center gap-2">
                      <span className={`text-2xl font-bold ${amountColor}`}>
                        {isExpense ? '−' : '+'}
                      </span>
                      <input
                        type="number"
                        value={editData.betrag}
                        onChange={(e) => setEditData({ ...editData, betrag: e.target.value })}
                        step="0.01"
                        className="w-36 rounded-lg border border-gray-200 bg-white px-2.5 py-1.5 text-2xl font-bold tabular-nums text-gray-900 transition-colors focus:border-primary-500 focus:ring-1 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-800 dark:text-white sm:w-[170px]"
                      />
                      <span className="text-base text-gray-400">EUR</span>
                    </div>
                  ) : (
                    <p
                      className={`mt-1 text-[32px] font-bold leading-[1.05] tracking-tight tabular-nums sm:text-[38px] ${amountColor}`}
                    >
                      {formatAmount(statement.betrag)}
                    </p>
                  )}
                  <p className="mt-1.5 text-[12.5px] text-gray-500 dark:text-gray-400 sm:text-[13px]">
                    {isExpense ? 'Ausgang' : 'Eingang'} · {kontoName}
                    {statement.name_zahlungsbeteiligter
                      ? ` · ${statement.name_zahlungsbeteiligter}`
                      : ''}
                  </p>
                </div>

                <div className="flex flex-shrink-0 items-center gap-2">
                  <span
                    className={`inline-flex h-[26px] items-center whitespace-nowrap rounded-full px-2.5 text-[11.5px] font-semibold sm:h-7 sm:px-3 sm:text-xs ${statusMeta.badge}`}
                  >
                    {statusMeta.label}
                  </span>
                  {!isEditing && (
                    <button
                      type="button"
                      onClick={enterEditMode}
                      aria-label="Buchung bearbeiten"
                      className="flex h-[34px] w-[34px] items-center justify-center gap-2 rounded-lg border border-gray-300 bg-white text-[13px] font-semibold text-gray-600 transition-colors hover:bg-gray-50 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700 sm:h-[38px] sm:w-auto sm:px-3.5"
                    >
                      <PencilIcon className="h-4 w-4" aria-hidden="true" />
                      <span className="hidden sm:inline">Bearbeiten</span>
                    </button>
                  )}
                </div>
              </div>

              {/* Kategorisierungs-Fortschritt (nur normale Buchungen) */}
              {!transfer && absBetrag > 0 && (
                <div className="mt-3.5 space-y-1.5">
                  <div className="flex items-center justify-between gap-3 text-[11.5px] text-gray-500 dark:text-gray-400 sm:text-xs">
                    <span className="truncate">
                      {formatCurrency(totalCategorized)} von {formatCurrency(absBetrag)}{' '}
                      kategorisiert
                    </span>
                    <span className={`flex-shrink-0 font-semibold tabular-nums ${statusMeta.text}`}>
                      {pct}%
                    </span>
                  </div>
                  <div className="h-1.5 overflow-hidden rounded-full bg-gray-200 dark:bg-gray-700">
                    <div
                      className={`h-full rounded-full transition-all duration-300 motion-reduce:transition-none ${statusMeta.bar}`}
                      style={{ width: `${pct}%` }}
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Reiter – nur mobil; ab `sm` stehen alle Abschnitte nebeneinander */}
            <div className="sticky top-0 z-10 -mx-4 mt-4 bg-gray-50 px-4 pb-1 pt-3 dark:bg-gray-900/40 sm:hidden">
              <div className="flex gap-1 rounded-xl bg-gray-100 p-[3px] dark:bg-gray-800">
                {tabs.map((t) => (
                  <button
                    key={t.key}
                    type="button"
                    onClick={() => setActiveTab(t.key)}
                    className={`h-[38px] flex-1 rounded-lg text-[13px] font-semibold transition-colors ${
                      currentTab === t.key
                        ? 'bg-white text-gray-900 shadow-sm dark:bg-[#1c2432] dark:text-white'
                        : 'text-gray-500 dark:text-gray-400'
                    }`}
                  >
                    {t.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="mt-4 grid grid-cols-1 gap-4 sm:mt-[18px] sm:grid-cols-[1.25fr_1fr] sm:items-start sm:gap-[18px]">
              {/* Linke Spalte: Buchungsdetails + Umbuchung */}
              <div className="contents sm:block sm:min-w-0 sm:space-y-[18px]">
                <SectionCard
                  icon={InformationCircleIcon}
                  title="Buchungsdetails"
                  className={tabPane('details')}
                  bodyClassName={isEditing ? 'space-y-3 p-4 sm:p-[18px]' : ''}
                >
                  {isEditing ? (
                    <>
                      <div>
                        <AttributeLabel iconKey="date" className="block mb-1" textClassName="text-xs font-medium text-gray-500 dark:text-gray-400">Buchungsdatum</AttributeLabel>
                        <input
                          type="date"
                          value={editData.buchungstag}
                          onChange={(e) => setEditData({ ...editData, buchungstag: e.target.value })}
                          className="w-full px-3 py-2.5 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg text-sm text-gray-900 dark:text-white focus:border-primary-500 focus:ring-1 focus:ring-primary-500 transition-colors"
                        />
                      </div>
                      <div>
                        <AttributeLabel iconKey="description" className="block mb-1" textClassName="text-xs font-medium text-gray-500 dark:text-gray-400">Buchungstext</AttributeLabel>
                        <input
                          type="text"
                          value={editData.buchungstext}
                          onChange={(e) => setEditData({ ...editData, buchungstext: e.target.value })}
                          className="w-full px-3 py-2.5 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg text-sm text-gray-900 dark:text-white focus:border-primary-500 focus:ring-1 focus:ring-primary-500 transition-colors"
                          placeholder="Buchungstext"
                        />
                      </div>
                      <div>
                        <AttributeLabel iconKey="purpose" className="block mb-1" textClassName="text-xs font-medium text-gray-500 dark:text-gray-400">Verwendungszweck</AttributeLabel>
                        <textarea
                          value={editData.verwendungszweck}
                          onChange={(e) => setEditData({ ...editData, verwendungszweck: e.target.value })}
                          rows={3}
                          className="w-full px-3 py-2.5 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg text-sm text-gray-900 dark:text-white focus:border-primary-500 focus:ring-1 focus:ring-primary-500 transition-colors resize-none"
                          placeholder="Verwendungszweck"
                        />
                      </div>
                      <div>
                        <AttributeLabel iconKey="name" className="block mb-1" textClassName="text-xs font-medium text-gray-500 dark:text-gray-400">Zahlungsbeteiligter</AttributeLabel>
                        <input
                          type="text"
                          value={editData.name_zahlungsbeteiligter}
                          onChange={(e) => setEditData({ ...editData, name_zahlungsbeteiligter: e.target.value })}
                          className="w-full px-3 py-2.5 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg text-sm text-gray-900 dark:text-white focus:border-primary-500 focus:ring-1 focus:ring-primary-500 transition-colors"
                          placeholder="Name des Zahlungsbeteiligten"
                        />
                      </div>
                      <div>
                        <AttributeLabel iconKey="date" className="block mb-1" textClassName="text-xs font-medium text-gray-500 dark:text-gray-400">Erstellt am</AttributeLabel>
                        <input
                          type="date"
                          value={editData.erstellt_am}
                          onChange={(e) => setEditData({ ...editData, erstellt_am: e.target.value })}
                          className="w-full px-3 py-2.5 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg text-sm text-gray-900 dark:text-white focus:border-primary-500 focus:ring-1 focus:ring-primary-500 transition-colors"
                        />
                      </div>
                    </>
                  ) : (
                    <dl className="divide-y divide-gray-100 dark:divide-gray-700/60">
                      {detailRows.map((row) => (
                        <div
                          key={row.key}
                          className={`gap-1 px-4 py-3 sm:grid sm:grid-cols-[150px_minmax(0,1fr)] sm:items-baseline sm:gap-4 sm:px-[18px] ${
                            row.stack
                              ? 'flex flex-col'
                              : 'grid grid-cols-[auto_minmax(0,1fr)] items-baseline gap-4'
                          }`}
                        >
                          <dt className="min-w-0">
                            <AttributeLabel
                              iconKey={row.iconKey}
                              size="sm"
                              textClassName="text-[12.5px] text-gray-500 dark:text-gray-400"
                            >
                              {row.label}
                            </AttributeLabel>
                          </dt>
                          <dd
                            className={`break-words text-[13.5px] font-semibold text-gray-800 dark:text-gray-100 sm:text-left ${
                              row.stack ? 'text-left' : 'text-right'
                            }`}
                          >
                            {row.value}
                          </dd>
                        </div>
                      ))}
                    </dl>
                  )}
                </SectionCard>

                {/* Umbuchung: Schalter statt separatem Knopf (Entwurf) */}
                <SectionCard
                  icon={ArrowsRightLeftIcon}
                  title="Umbuchung"
                  className={tabPane('details')}
                  bodyClassName="space-y-3 p-4 sm:p-[18px]"
                >
                  <div className="flex items-center justify-between gap-4">
                    <p className="text-[12.5px] leading-relaxed text-gray-600 dark:text-gray-300 sm:text-[13px]">
                      Geld zwischen zwei Vereinskonten verschoben? Dann zählt es weder als Einnahme
                      noch als Ausgabe.
                    </p>
                    <SwitchButton
                      checked={transfer}
                      disabled={umbuchungLaeuft}
                      label="Als Umbuchung kennzeichnen"
                      onClick={transfer ? umbuchungAufheben : umbuchungOeffnen}
                    />
                  </div>

                  {transfer && (
                    <div className="space-y-2 border-t border-gray-100 pt-3 dark:border-gray-700/60">
                      <p className="text-[11px] font-semibold uppercase tracking-[0.06em] text-gray-400 dark:text-gray-500">
                        Beteiligte Konten
                      </p>
                      <div className="flex items-center gap-3 rounded-xl border border-gray-200 p-3 dark:border-gray-700">
                        <div className="flex h-[30px] w-[30px] flex-shrink-0 items-center justify-center rounded-lg bg-error-50 dark:bg-error-900/25">
                          <ArrowUpIcon className="h-[15px] w-[15px] text-error-600 dark:text-error-400" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="text-[11.5px] text-gray-500 dark:text-gray-400">Von Konto</p>
                          <p className="truncate text-[13.5px] font-semibold text-gray-800 dark:text-gray-100">
                            {getAccountName(statement.von_konto_id)}
                          </p>
                        </div>
                        <span className="flex-shrink-0 text-[13px] font-semibold tabular-nums text-error-600 dark:text-error-400">
                          −{formatCurrency(absBetrag)}
                        </span>
                      </div>
                      <div className="flex items-center gap-3 rounded-xl border border-gray-200 p-3 dark:border-gray-700">
                        <div className="flex h-[30px] w-[30px] flex-shrink-0 items-center justify-center rounded-lg bg-success-50 dark:bg-success-900/25">
                          <ArrowDownIcon className="h-[15px] w-[15px] text-success-600 dark:text-success-400" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="text-[11.5px] text-gray-500 dark:text-gray-400">Nach Konto</p>
                          <p className="truncate text-[13.5px] font-semibold text-gray-800 dark:text-gray-100">
                            {getAccountName(statement.nach_konto_id)}
                          </p>
                        </div>
                        <span className="flex-shrink-0 text-[13px] font-semibold tabular-nums text-success-600 dark:text-success-400">
                          +{formatCurrency(absBetrag)}
                        </span>
                      </div>
                    </div>
                  )}
                </SectionCard>
              </div>

              {/* Rechte Spalte: Kategorien + Belege + Tags */}
              <div className="contents sm:block sm:min-w-0 sm:space-y-[18px]">
                {!transfer && (
                  <SectionCard
                    icon={TagIcon}
                    title={`Kategorien (${categories.length})`}
                    className={tabPane('cats')}
                    right={
                      <span className="flex-shrink-0 text-xs tabular-nums text-gray-500 dark:text-gray-400">
                        offen {formatCurrency(Math.max(0, remainingAmount))}
                      </span>
                    }
                  >
                    {categories.map((cat) => {
                      const Icon = getCategoryIcon(cat.kategorie_id);
                      return (
                        <div
                          key={cat.id}
                          className="flex items-center gap-3 rounded-xl border border-gray-200 p-2.5 dark:border-gray-700"
                        >
                          <div className="flex h-[30px] w-[30px] flex-shrink-0 items-center justify-center rounded-lg bg-primary-50 text-primary-600 dark:bg-primary-900/25 dark:text-primary-400">
                            <Icon className="h-[15px] w-[15px]" />
                          </div>
                          <div className="min-w-0 flex-1">
                            <p className="truncate text-[13.5px] font-semibold text-gray-800 dark:text-gray-100">
                              {getCategoryName(cat.kategorie_id)}
                            </p>
                            <p className="truncate text-xs tabular-nums text-gray-500 dark:text-gray-400">
                              {formatCurrency(cat.betrag)}
                              {cat.kategorie_id === 1 && cat.member_id
                                ? ` · ${getMemberName(cat.member_id)}${cat.beitrag_jahr ? ` (${cat.beitrag_jahr})` : ''}`
                                : ''}
                            </p>
                            {cat.kategorie_beschreibung && (
                              <p className="mt-1 line-clamp-2 text-[11px] leading-relaxed text-gray-400 dark:text-gray-500">
                                {cat.kategorie_beschreibung}
                              </p>
                            )}
                          </div>
                          <button
                            type="button"
                            onClick={() => deleteCategory(cat.id)}
                            aria-label={`${getCategoryName(cat.kategorie_id)} entfernen`}
                            className="flex h-[30px] w-[30px] flex-shrink-0 items-center justify-center rounded-lg text-gray-400 transition-colors hover:bg-error-50 hover:text-error-600 dark:hover:bg-error-900/20 dark:hover:text-error-400"
                          >
                            <XMarkIcon className="h-[15px] w-[15px]" />
                          </button>
                        </div>
                      );
                    })}

                    {/* Zuordnungs-Formular */}
                    {isAddingCategory ? (
                      <div className="space-y-3 rounded-xl border border-gray-200 bg-gray-50 p-3.5 dark:border-gray-600 dark:bg-gray-700/30">
                        <div>
                          {/* Kategorie — Combobox statt selbstgebautem Dropdown:
                              das alte war `absolute` im Modal-Rumpf und wurde
                              von dessen overflow abgeschnitten. */}
                          <AttributeLabel iconKey="category" className="block mb-1" textClassName="text-xs font-medium text-gray-500 dark:text-gray-400">Kategorie</AttributeLabel>
                          <div className="flex items-stretch gap-2">
                          <button
                            type="button"
                            onClick={askCategoryAssistant}
                            disabled={categorySuggestionLoading}
                            aria-label="KI nach Kategorie fragen"
                            title="KI nach Kategorie fragen – Buchungstext und Zahlungsdaten werden an den KI-Dienst gesendet"
                            className="flex w-10 flex-shrink-0 items-center justify-center rounded-lg border border-primary-200 bg-primary-50 text-primary-600 transition-colors hover:bg-primary-100 disabled:cursor-wait disabled:opacity-70 dark:border-primary-800 dark:bg-gray-800 dark:text-primary-400 dark:hover:bg-gray-700"
                          >
                            {categorySuggestionLoading ? (
                              <ArrowPathIcon className="h-4 w-4 animate-spin" aria-hidden="true" />
                            ) : (
                              <SparklesIcon className="h-4 w-4" aria-hidden="true" />
                            )}
                          </button>
                          <div className="min-w-0 flex-1">
                          <Combobox
                            label="Kategorie"
                            placeholder={categorySuggestionLoading ? 'KI prüft Buchung …' : 'Kategorie wählen …'}
                            value={selectedCategoryId || null}
                            options={sortedCategories.map((c) => {
                              const prozent = categorySuggestion?.probabilities?.[c.id]
                                ?? (categorySuggestion?.category_id === c.id ? categorySuggestion.confidence : undefined);
                              return {
                                id: c.id,
                                label: c.name,
                                icon: getCategoryIcon(c.id),
                                badge: prozent !== undefined ? `${prozent} %` : undefined,
                              };
                            })}
                            onChange={(id) => {
                              setSelectedCategoryId(Number(id));
                              if (Number(id) !== 1) setCategoryMemberId(null);
                            }}
                          />
                          </div>
                          </div>
                          {categorySuggestionError && (
                            <p className="mt-1.5 text-[11.5px] leading-relaxed text-error-600 dark:text-error-400">
                              {categorySuggestionError}
                            </p>
                          )}
                          {categorySuggestion && selectedCategoryId === categorySuggestion.category_id && (
                            <p className={`mt-1.5 flex items-center gap-1.5 text-[11.5px] font-medium ${
                              categorySuggestion.needs_review
                                ? 'text-warning-700 dark:text-warning-300'
                                : 'text-primary-700 dark:text-primary-300'
                            }`}>
                              <SparklesIcon className="h-3.5 w-3.5 flex-shrink-0" aria-hidden="true" />
                              KI-Vorschlag, {categorySuggestion.probabilities?.[categorySuggestion.category_id] ?? categorySuggestion.confidence} %
                              {categorySuggestion.needs_review && ' – bitte prüfen'}
                            </p>
                          )}
                        </div>

                        <div>
                          <div className="mb-1 flex items-center justify-between gap-2">
                            <AttributeLabel iconKey="amount" textClassName="text-xs font-medium text-gray-500 dark:text-gray-400">Betrag</AttributeLabel>
                            {hasRest && (
                              <button
                                type="button"
                                onClick={() => setCategoryAmount(remainingAmount.toFixed(2))}
                                className="text-xs font-semibold text-primary-600 hover:underline dark:text-primary-400"
                              >
                                Restbetrag übernehmen
                              </button>
                            )}
                          </div>
                          <div className="relative">
                            <input
                              type="number"
                              min="0.01"
                              max={Math.max(0, remainingAmount)}
                              value={categoryAmount}
                              onChange={(e) => setCategoryAmount(e.target.value)}
                              className="w-full rounded-lg border border-gray-200 bg-white px-3 py-3 text-sm tabular-nums text-gray-900 transition-colors hover:border-primary-400 focus:border-primary-500 focus:ring-1 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-800 dark:text-white"
                              step="0.01"
                              placeholder="0,00"
                            />
                            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-gray-400">EUR</span>
                          </div>
                        </div>

                        {/* Mitglied — Combobox mit Suche: bei 80 Mitgliedern
                            ist Tippen der einzig brauchbare Weg. */}
                        {selectedCategoryId === 1 && (
                          <div>
                            <AttributeLabel iconKey="member" className="block mb-1" textClassName="text-xs font-medium text-gray-500 dark:text-gray-400">Mitglied</AttributeLabel>
                            <Combobox
                              label="Mitglied"
                              placeholder="Mitglied wählen …"
                              value={categoryMemberId}
                              options={sortedMembers.map((m: any) => ({
                                id: m.member_id,
                                label: `${m.vorname} ${m.name}${m.geloescht ? ' (ausgeschieden)' : ''}`,
                                icon: UserIcon,
                              }))}
                              onChange={(id) => setCategoryMemberId(Number(id))}
                            />
                          </div>
                        )}

                        {/* Beitragsjahr-Auswahl (nur Kategorie 1 = Mitgliederbeiträge) */}
                        {selectedCategoryId === 1 && (
                          <div>
                            <label className="mb-1 block text-xs font-medium text-gray-500 dark:text-gray-400">Beitragsjahr</label>
                            <div className="flex items-center gap-2">
                              {[categoryBeitragJahr - 1, categoryBeitragJahr, categoryBeitragJahr + 1].map((year) => (
                                <button
                                  key={year}
                                  type="button"
                                  onClick={() => setCategoryBeitragJahr(year)}
                                  className={`flex-1 rounded-lg border px-3 py-2 text-sm transition-colors ${
                                    categoryBeitragJahr === year
                                      ? 'border-primary-600 bg-primary-600 text-white'
                                      : 'border-gray-200 bg-white text-gray-700 hover:border-primary-400 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-300'
                                  }`}
                                >
                                  {year}
                                </button>
                              ))}
                            </div>
                          </div>
                        )}

                        <div className="flex gap-2 pt-1">
                          <button
                            type="button"
                            onClick={() => {
                              setIsAddingCategory(false);
                              setSelectedCategoryId(0);
                              setCategoryAmount('');
                              setCategoryMemberId(null);
                              setCategoryBeitragJahr(new Date().getFullYear());
                            }}
                            className="flex-1 rounded-lg border border-gray-200 bg-white py-2.5 text-sm text-gray-700 transition-colors hover:bg-gray-50 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700"
                          >
                            Abbrechen
                          </button>
                          <button
                            type="button"
                            onClick={addCategory}
                            disabled={!selectedCategoryId || !categoryAmount || (selectedCategoryId === 1 && !categoryMemberId)}
                            className="flex-1 rounded-lg bg-primary-600 py-2.5 text-sm font-medium text-white transition-colors hover:bg-primary-700 disabled:cursor-not-allowed disabled:bg-gray-300 dark:disabled:bg-gray-600"
                          >
                            Hinzufügen
                          </button>
                        </div>
                      </div>
                    ) : (
                      hasRest && (
                        <DashedAction
                          icon={PlusIcon}
                          onClick={() => {
                            setIsAddingCategory(true);
                            setCategoryAmount(remainingAmount.toFixed(2));
                            setCategoryBeitragJahr(new Date().getFullYear());
                          }}
                        >
                          Kategorie zuordnen
                        </DashedAction>
                      )
                    )}

                    {hasRest && (
                      <div className="flex items-center gap-2 rounded-xl bg-warning-50 px-3 py-2.5 text-warning-800 dark:bg-warning-900/20 dark:text-warning-300">
                        <ExclamationTriangleIcon className="h-4 w-4 flex-shrink-0" />
                        <p className="text-xs font-semibold">
                          {formatCurrency(remainingAmount)} noch nicht zugeordnet
                        </p>
                      </div>
                    )}
                  </SectionCard>
                )}

                {!transfer && (
                  <SectionCard
                    icon={DocumentTextIcon}
                    title={`Belege (${documents.length})`}
                    className={tabPane('docs')}
                  >
                    {documents.map((doc) => {
                      const isImage =
                        doc.file_type?.startsWith('image/') ||
                        doc.name?.match(/\.(jpg|jpeg|png|gif|webp)$/i) ||
                        doc.rechnung_pfad?.match(/\.(jpg|jpeg|png|gif|webp)$/i);

                      return (
                        <div
                          key={doc.id}
                          role="button"
                          tabIndex={0}
                          onClick={() => handleDocumentClick(doc.id)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              handleDocumentClick(doc.id);
                            }
                          }}
                          className="flex min-w-0 cursor-pointer items-center gap-3 overflow-hidden rounded-xl border border-gray-200 p-2.5 transition-colors hover:bg-gray-50 dark:border-gray-700 dark:hover:bg-gray-700/30"
                        >
                          <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-300">
                            {isImage ? (
                              <PhotoIcon className="h-4 w-4" />
                            ) : (
                              <DocumentIcon className="h-4 w-4" />
                            )}
                          </div>
                          <div className="min-w-0 flex-1">
                            <p
                              title={doc.name || doc.rechnung_pfad || 'Beleg'}
                              className="truncate text-[13px] font-semibold leading-snug text-gray-800 dark:text-gray-100"
                            >
                              {doc.name || doc.rechnung_pfad || 'Beleg'}
                            </p>
                            <p className="truncate text-[11.5px] text-gray-500 dark:text-gray-400">
                              {isImage ? 'Bild' : 'PDF'}
                              {doc.file_size ? ` · ${Math.round(doc.file_size / 1024)} kB` : ''}
                            </p>
                          </div>
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              deleteDocument(doc.id);
                            }}
                            aria-label="Beleg löschen"
                            className="flex h-[30px] w-[30px] flex-shrink-0 items-center justify-center rounded-lg text-gray-400 transition-colors hover:bg-error-50 hover:text-error-600 dark:hover:bg-error-900/20 dark:hover:text-error-400"
                          >
                            <TrashIcon className="h-[15px] w-[15px]" />
                          </button>
                        </div>
                      );
                    })}

                    <DashedAction icon={PaperClipIcon} onClick={() => setShowInvoiceMatcher(true)}>
                      Beleg hinzufügen
                    </DashedAction>
                  </SectionCard>
                )}

                <SectionCard
                  icon={HashtagIcon}
                  title={`Tags (${statementTags.length})`}
                  className={tabPane('docs')}
                >
                  <div className="flex flex-wrap gap-2">
                    {statementTags.map((tag) => (
                      <button
                        key={tag}
                        type="button"
                        onClick={() => handleRemoveTag(tag)}
                        aria-label={`Tag ${tag} entfernen`}
                        className="inline-flex h-8 items-center gap-1.5 rounded-full border border-primary-200 bg-primary-50 pl-3 pr-2.5 text-[12.5px] font-semibold text-primary-700 transition-colors hover:border-error-200 hover:bg-error-50 hover:text-error-700 dark:border-primary-800 dark:bg-primary-900/25 dark:text-primary-300 dark:hover:border-error-800 dark:hover:bg-error-900/20 dark:hover:text-error-300"
                      >
                        {tag}
                        <XMarkIcon className="h-3.5 w-3.5" aria-hidden="true" />
                      </button>
                    ))}
                    {availableBuchungTags
                      .filter((t) => !statementTags.includes(t))
                      .map((tag) => (
                        <button
                          key={tag}
                          type="button"
                          onClick={() => handleAddTag(tag)}
                          className="inline-flex h-8 items-center gap-1 rounded-full border border-dashed border-gray-300 px-3 text-[12.5px] text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-700 dark:border-gray-600 dark:text-gray-400 dark:hover:bg-gray-700 dark:hover:text-gray-200"
                        >
                          + {tag}
                        </button>
                      ))}
                    <button
                      type="button"
                      onClick={() => setShowTagPicker(!showTagPicker)}
                      className={`inline-flex h-8 items-center gap-1 rounded-full border px-3 text-[12.5px] font-semibold transition-colors ${
                        showTagPicker
                          ? 'border-primary-200 bg-primary-50 text-primary-700 dark:border-primary-800 dark:bg-primary-900/25 dark:text-primary-300'
                          : 'border-dashed border-gray-300 text-gray-500 hover:bg-gray-100 dark:border-gray-600 dark:text-gray-400 dark:hover:bg-gray-700'
                      }`}
                    >
                      <PlusIcon
                        className={`h-3.5 w-3.5 transition-transform ${showTagPicker ? 'rotate-45' : ''}`}
                      />
                      Neuer Tag
                    </button>
                  </div>

                  {showTagPicker && (
                    <div className="flex items-center gap-2 pt-1">
                      <input
                        type="text"
                        value={newTagInput}
                        onChange={(e) => setNewTagInput(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' && newTagInput.trim()) {
                            handleAddTag(newTagInput.trim());
                            setNewTagInput('');
                          }
                          if (e.key === 'Escape') {
                            setShowTagPicker(false);
                            setNewTagInput('');
                          }
                        }}
                        placeholder="Neuer Tag …"
                        className="h-10 min-w-0 flex-1 rounded-lg border border-gray-200 bg-white px-3 text-sm text-gray-900 focus:border-primary-500 focus:ring-1 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-800 dark:text-white"
                      />
                      <button
                        type="button"
                        onClick={() => {
                          if (!newTagInput.trim()) return;
                          handleAddTag(newTagInput.trim());
                          setNewTagInput('');
                        }}
                        disabled={!newTagInput.trim()}
                        className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-primary-600 text-white transition-colors hover:bg-primary-700 disabled:cursor-not-allowed disabled:bg-gray-300 dark:disabled:bg-gray-600"
                        aria-label="Tag hinzufügen"
                      >
                        <CheckIcon className="h-4 w-4" />
                      </button>
                    </div>
                  )}
                </SectionCard>
              </div>
            </div>
          </div>
        ) : null}
      </ModalShell>

      {/* Umbuchung kennzeichnen */}
      <ModalShell
        open={umbuchungOffen}
        onClose={() => setUmbuchungOffen(false)}
        variant="choice"
        size="md"
        icon={ArrowsRightLeftIcon}
        title="Als Umbuchung kennzeichnen"
        subtitle={statement ? `${formatCurrency(Math.abs(statement.betrag))} · ${formatDate(statement.buchungstag)}` : undefined}
        footer={
          <ModalActions
            secondary={{ label: 'Abbrechen', onClick: () => setUmbuchungOffen(false) }}
            primary={{
              label: umbuchungLaeuft ? 'Wird gesetzt …' : 'Kennzeichnen',
              onClick: umbuchungSpeichern,
              disabled: !zielKontoId || umbuchungLaeuft,
            }}
          />
        }
      >
        <div className="space-y-4">
          <p className="text-[12.5px] leading-relaxed text-gray-500 dark:text-gray-400">
            Die Buchung bleibt unverändert — Betrag, Datum, Verwendungszweck, Belege und Kategorien
            werden nicht angefasst. Gesetzt wird nur das Kennzeichen, damit der Betrag nicht mehr als
            Einnahme oder Ausgabe zählt.
          </p>

          {/* Gegenbuchung: im Regelfall existiert sie bereits */}
          <div>
            <label className="mb-1.5 block text-[11px] font-bold uppercase tracking-wider text-gray-500 dark:text-gray-400">
              Gegenbuchung auf dem anderen Konto
            </label>

            {ladeKandidaten ? (
              <p className="py-3 text-[13px] text-gray-500 dark:text-gray-400">Wird gesucht …</p>
            ) : kandidaten.length > 0 ? (
              <div className="overflow-hidden rounded-xl border border-gray-200 dark:border-gray-700 divide-y divide-gray-200 dark:divide-gray-700">
                {kandidaten.map((k) => (
                  <button
                    key={k.id}
                    type="button"
                    onClick={() => {
                      setGegenbuchungId(k.id);
                      setZielKontoId(k.konto_id);
                    }}
                    className={`flex w-full items-start gap-3 px-3.5 py-2.5 text-left transition-colors ${
                      gegenbuchungId === k.id
                        ? 'bg-primary-50 dark:bg-primary-900/20'
                        : 'hover:bg-gray-50 dark:hover:bg-gray-700/40'
                    }`}
                  >
                    <span
                      className={`mt-1 h-3.5 w-3.5 flex-shrink-0 rounded-full border-2 ${
                        gegenbuchungId === k.id
                          ? 'border-primary-600 bg-primary-600'
                          : 'border-gray-300 dark:border-gray-600'
                      }`}
                      aria-hidden="true"
                    />
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-[13px] font-semibold text-gray-900 dark:text-white">
                        {k.konto_name} · {formatCurrency(k.betrag)}
                      </span>
                      <span className="block truncate text-[11.5px] text-gray-500 dark:text-gray-400">
                        {formatDate(k.buchungstag)}
                        {k.tage_abstand > 0 && ` (${k.tage_abstand} Tage Abstand)`}
                        {k.verwendungszweck ? ` · ${k.verwendungszweck}` : ''}
                      </span>
                    </span>
                  </button>
                ))}
              </div>
            ) : (
              <p className="rounded-lg border border-dashed border-gray-300 dark:border-gray-600 px-3.5 py-3 text-[12.5px] text-gray-500 dark:text-gray-400">
                Keine passende Gegenbuchung gefunden. Wähle unten das Zielkonto — die fehlende
                Gegenbuchung wird dann angelegt, damit das Geld nicht verschwindet.
              </p>
            )}
          </div>

          {/* Zielkonto — bei gewählter Gegenbuchung ergibt es sich daraus */}
          <div>
            <label className="mb-1.5 block text-[11px] font-bold uppercase tracking-wider text-gray-500 dark:text-gray-400">
              {Number(statement?.betrag) < 0 ? 'Geld ging auf dieses Konto' : 'Geld kam von diesem Konto'}
            </label>
            <select
              value={zielKontoId ?? ''}
              onChange={(e) => {
                const neu = e.target.value ? Number(e.target.value) : null;
                setZielKontoId(neu);
                // Passt die gewählte Gegenbuchung nicht mehr zum Konto, verwerfen.
                if (gegenbuchungId && kandidaten.find((k) => k.id === gegenbuchungId)?.konto_id !== neu) {
                  setGegenbuchungId(null);
                }
              }}
              className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-[13px] text-gray-900 dark:border-gray-600 dark:bg-gray-800 dark:text-white"
            >
              <option value="">Konto wählen …</option>
              {accounts
                .filter((a) => a.id !== statement?.konto_id)
                .map((a) => (
                  <option key={a.id} value={a.id}>{a.name}</option>
                ))}
            </select>
          </div>

          {!gegenbuchungId && zielKontoId && (
            <p className="rounded-lg border border-warning-200 bg-warning-50 px-3.5 py-2.5 text-[12.5px] text-warning-800 dark:border-warning-900/40 dark:bg-warning-900/20 dark:text-warning-200">
              Es wird eine neue Gegenbuchung über {statement ? formatCurrency(Math.abs(statement.betrag)) : ''} auf
              „{getAccountName(zielKontoId)}" angelegt.
            </p>
          )}

          {umbuchungFehler && (
            <p className="rounded-lg bg-error-50 px-3.5 py-2.5 text-[13px] font-semibold text-error-700 dark:bg-error-900/20 dark:text-error-300">
              {umbuchungFehler}
            </p>
          )}
        </div>
      </ModalShell>

      {/* Confirmation Modal */}
      <ConfirmationModal
        visible={confirmModal.visible}
        onClose={() => setConfirmModal({ ...confirmModal, visible: false })}
        type={confirmModal.type}
        title={confirmModal.title}
        message={confirmModal.message}
        variant={confirmModal.variant}
        button={confirmModal.variant === 'single' ? {
          text: 'OK',
          onPress: () => setConfirmModal({ ...confirmModal, visible: false })
        } : undefined}
        buttons={confirmModal.variant === 'two' ? {
          cancel: {
            text: 'Abbrechen',
            onPress: () => setConfirmModal({ ...confirmModal, visible: false })
          },
          confirm: {
            text: 'Löschen',
            onPress: () => {
              confirmDeleteStatement();
              setConfirmModal({ ...confirmModal, visible: false });
            }
          }
        } : undefined}
      />

      {/* Invoice Matcher Modal */}
      {showInvoiceMatcher && (
        <InvoiceMatcher
          onFilesSelected={handleFilesSelected}
          initialAmount={Math.abs(statement?.betrag || 0)}
          preSelectedDocuments={documents.map(doc => doc.id)}
          onClose={() => setShowInvoiceMatcher(false)}
        />
      )}

      {/* Document Detail Modal */}
      {showDocumentDetail && (
        <DocumentDetailModal
          documentId={showDocumentDetail}
          onClose={() => setShowDocumentDetail(null)}
          onDelete={handleDocumentDelete}
          onAIScan={handleDocumentAIScan}
          onDownload={handleDocumentDownload}
        />
      )}
    </>
  );
};

export default StatementDetailModal;
